import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Plus } from 'lucide-react';
import { 
  getEligibleAthletes, 
  getStartListDetails,
  saveHeats,
  saveSemis,
  saveFinals
} from 'api/eventStartListApi';
import { useParams, useNavigate } from 'react-router-dom';
import routes from 'routes';
import styles from './StartListsEditor.module.css';
import AthletesPanel from './AthletesPanel';
import HeatsSection from './HeatsSection';
import AutoGenerateModal from './AutoGenerateModal';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import PageHeader from 'components/NewDesign/PageHeader/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { useBlocker } from 'hooks/useChangeDetection';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupRange } from 'utils/groupLabel';
import { toast } from 'react-toastify';

const ROUND_TYPES = ['Heat', 'Semi', 'Final'];

const StartListEditor = () => {
  const { clubId, meetId, eventTypeCode, ageGroup, gender } = useParams();
  // A group with no name in the meet program is titled by its stored range, shown as the club reads it.
  const { groupingBasis } = useGroupLabels();
  const navigate = useNavigate();
  
  // Round and settings state
  const [activeRound, setActiveRound] = useState('Heat');
  const [selectedLanes, setSelectedLanes] = useState([]);
  //const [setAvailableLanes] = useState([]);
  //const [minAthletes, setMinAthletes] = useState(2);
  const [maxAthletes, setMaxAthletes] = useState(8);
  
  // NEW: Event mode state
  const [isMultilaneEvent, setIsMultilaneEvent] = useState(true);
  
  // Heats state for current round
  const [heats, setHeats] = useState([]);
  
  // All athletes and available athletes
  const [allAthletes, setAllAthletes] = useState([]);
  const [availableAthletes, setAvailableAthletes] = useState([]);
  
  // Selection state
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [selectedLaneAthlete, setSelectedLaneAthlete] = useState(null);
  const [sortBy, setSortBy] = useState('sb');
  
  // Modal state
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingRound, setPendingRound] = useState(null);
  
  // API data state
  const [eventDetails, setEventDetails] = useState(null);
  const [roundsData, setRoundsData] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const heatsInitialized = useRef(false);
  const originalHeatsRef = useRef(null);
  const pendingNavigationRef = useRef(null);

  useBlocker((retryFn) => {
    if (hasUnsavedChanges) {
      pendingNavigationRef.current = retryFn;
      setShowUnsavedModal(true);
      return false;
    }
    return true;
  }, true);

  // UPDATED: Generate empty heats/groups based on event mode
  const generateEmptyHeats = useCallback((athletes) => {
    const numAthletes = athletes.length;
    
    // For group mode, use positions 1-30 (fixed max)
    // For multilane, use selected lanes or default 1-8
    const positions = isMultilaneEvent 
      ? (selectedLanes.length > 0 ? selectedLanes : [1,2,3,4,5,6,7,8])
      : Array.from({ length: 30 }, (_, i) => i + 1); // 30 positions for group mode
    
    const positionsPerGroup = positions.length;
    const numGroups = isMultilaneEvent 
      ? Math.max(1, Math.ceil(numAthletes / Math.min(positionsPerGroup, maxAthletes)))
      : Math.max(1, Math.ceil(numAthletes / positionsPerGroup));

    const generatedHeats = [];
    for (let i = 0; i < numGroups; i++) {
      const heat = {
        id: i + 1,
        roundType: activeRound,
        roundNumber: i + 1,
        lanes: Object.fromEntries(positions.map((pos) => [pos, null]))
      };
      generatedHeats.push(heat);
    }

    setHeats(generatedHeats);
    setAvailableAthletes(athletes);
    originalHeatsRef.current = JSON.stringify(generatedHeats);
    setHasUnsavedChanges(false);
    heatsInitialized.current = true;
  }, [selectedLanes, isMultilaneEvent, activeRound, maxAthletes]);

  // Load round data into state
  const loadRoundData = useCallback((roundData, athletes) => {
    if (roundData && roundData.heats && roundData.heats.length > 0) {
      setHeats(roundData.heats);
      
      // Calculate available athletes (not assigned to this round)
      const assignedAthleteIDs = new Set();
      roundData.heats.forEach(heat => {
        Object.values(heat.lanes).forEach(athlete => {
          if (athlete) assignedAthleteIDs.add(athlete.athleteID);
        });
      });
      
      const available = athletes.filter(a => !assignedAthleteIDs.has(a.athleteID));
      setAvailableAthletes(available);
      
      // Store original state for change detection
      originalHeatsRef.current = JSON.stringify(roundData.heats);
      setHasUnsavedChanges(false);
    } else {
      // No data for this round - initialize empty
      generateEmptyHeats(athletes);
    }
    
    heatsInitialized.current = true;
  }, [generateEmptyHeats]);

  // Fetch eligible athletes (all athletes for this event)
  const fetchEligibleAthletes = useCallback(async () => {
    try {
      const response = await getEligibleAthletes(meetId, eventTypeCode, ageGroup, gender);
      const athletes = response?.data || [];
      setAllAthletes(athletes);
      return athletes;
    } catch (err) {
      console.error('[StartListEditor] Fetch eligible athletes error:', err);
      
      const errorMessage = err.response?.data?.message 
        || err.response?.data 
        || err.message 
        || 'Failed to load eligible athletes';
      
      alert(`Error: ${errorMessage}`);
      
      return [];
    }
  }, [meetId, eventTypeCode, ageGroup, gender]);

  // Fetch generation settings (lanes, min/max athletes)
  const fetchStartListGenerationSettings = useCallback(async () => {
    // Set default values instead of API call
    const defaultLanes = [1, 2, 3, 4, 5, 6, 7, 8];
    //setAvailableLanes(defaultLanes);
    setSelectedLanes(defaultLanes);
    //setMinAthletes(2);
    setMaxAthletes(8);
  }, []);

  // Transform API rounds to UI state
  const transformApiRoundsToState = useCallback((apiRounds, athletes) => {
    const heats = apiRounds.map(round => {
      // Build lanes object for this heat
      const lanes = {};
      
      round.assignments.forEach(assignment => {
        // Find full athlete data by athleteID
        const athlete = athletes.find(a => a.athleteID === assignment.athleteID);
        const position = assignment.laneNumber || assignment.positionNumber;
      
        if (athlete && position) {
          lanes[position] = {
            ...athlete,
            startListAssignmentID: assignment.startListAssignmentID,
            seedValue: assignment.seedValue
          };
        }
      });

      return {
        id: round.roundNumber,
        startListRoundID: round.startListRoundID,
        roundType: round.roundType,
        roundNumber: round.roundNumber,
        scheduledStartTime: round.scheduledStartTime,
        poolName: round.poolName,
        status: round.status,
        lanes: lanes
      };
    });

    return { heats };
  }, []);

  // UPDATED: Fetch existing start list details
 const fetchStartListDetails = useCallback(async (athletes) => {
  try {
    const response = await getStartListDetails(meetId, eventTypeCode, ageGroup, gender);
    const data = response?.data;
    
    if (data) {
      // Extract isMultilaneEvent from API
      setIsMultilaneEvent(data.isMultilaneEvent);
      
      // MOVED UP: Update selectedLanes BEFORE loading round data
      if (!data.isMultilaneEvent) {
        // For group mode, set 30 positions
        const groupPositions = Array.from({ length: 30 }, (_, i) => i + 1);
        setSelectedLanes(groupPositions);
      }
      
      setEventDetails({
        eventTypeName: data.eventTypeName,
        totalAthletes: data.totalAthletes,
        ageGroup: data.ageGroup,
        groupName: data.groupName,
        gender: data.gender,
        isMultilaneEvent: data.isMultilaneEvent
      });

      // Process rounds data - group by roundType
      const processedRounds = {
        Heat: [],
        Semi: [],
        Final: []
      };

      // Group API rounds by roundType
      (data.rounds || []).forEach(round => {
        if (round.roundType === 'Heat' || round.roundType === 'Semi' || round.roundType === 'Final') {
          if (!processedRounds[round.roundType]) {
            processedRounds[round.roundType] = [];
          }
          processedRounds[round.roundType].push(round);
        }
      });

      // Transform each round type's data
      const transformedRounds = {};
      Object.entries(processedRounds).forEach(([roundType, rounds]) => {
        if (rounds.length > 0) {
          transformedRounds[roundType] = transformApiRoundsToState(rounds, athletes);
        }
      });
      
      setRoundsData(transformedRounds);
      
      // IMPORTANT: Use setTimeout to ensure selectedLanes state update has completed
      // before loading round data (which will use the updated selectedLanes)
      setTimeout(() => {
        // Load the active round's data
        if (transformedRounds[activeRound]) {
          loadRoundData(transformedRounds[activeRound], athletes);
        } else {
          // No data for active round - generate empty
          generateEmptyHeats(athletes);
        }
      }, 0);
    }
  } catch (err) {
    console.error('[StartListEditor] Fetch start list details error:', err);
    
    const errorMessage = err.response?.data?.message 
      || err.response?.data 
      || err.message 
      || 'Failed to load start list details';
    
    alert(`Error: ${errorMessage}`);
  }
}, [meetId, eventTypeCode, ageGroup, gender, activeRound, generateEmptyHeats, loadRoundData, transformApiRoundsToState]);


  // Check if current state differs from original
  const checkForChanges = useCallback(() => {
    if (!originalHeatsRef.current) return false;
    const currentState = JSON.stringify(heats);
    return currentState !== originalHeatsRef.current;
  }, [heats]);

  // Update unsaved changes flag whenever heats change
  useEffect(() => {
    if (heatsInitialized.current) {
      setHasUnsavedChanges(checkForChanges());
    }
  }, [heats, checkForChanges]);

  // Initial data load - only run once on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const athletes = await fetchEligibleAthletes();
      await fetchStartListGenerationSettings();
      await fetchStartListDetails(athletes);
      setLoading(false);
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle round switching
  const handleRoundClick = (round) => {
    if (round === activeRound) return;
    
    if (hasUnsavedChanges) {
      setPendingRound(round);
      setShowUnsavedModal(true);
    } else {
      switchToRound(round);
    }
  };

  // Switch to a different round
  const switchToRound = (round) => {
    setActiveRound(round);
    heatsInitialized.current = false;
    
    // Load data for the new round
    if (roundsData[round]) {
      loadRoundData(roundsData[round], allAthletes);
    } else {
      generateEmptyHeats(allAthletes);
    }
  };

  // Discard changes and switch rounds
  const handleDiscardAndSwitch = () => {
    setShowUnsavedModal(false);
    if (pendingNavigationRef.current) {
      const retry = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      retry();
      return;
    }

    if (pendingRound) {
      switchToRound(pendingRound);
      setPendingRound(null);
    }
  };

  // Save current changes and switch rounds
  const handleSaveAndSwitch = async () => {
    await handleSave();
    setShowUnsavedModal(false);

    if (pendingNavigationRef.current) {
      const retry = pendingNavigationRef.current;
      pendingNavigationRef.current = null;
      retry();
      return;
    }

    if (pendingRound) {
      switchToRound(pendingRound);
      setPendingRound(null);
    }
  };

  // Transform heats state to API format
  const transformStateToApi = () => {
    const roundsArray = heats
      .map((heat, index) => {
        const assignments = [];
        
        Object.entries(heat.lanes).forEach(([laneNum, athlete]) => {
          if (athlete) {
            if (!athlete.athleteClubMembershipID) {
              console.error('Missing athleteClubMembershipID for athlete:', athlete);
            }
            
            assignments.push({
              athleteClubMembershipID: athlete.athleteClubMembershipID,
              laneNumber: isMultilaneEvent ? parseInt(laneNum) : null,
              positionNumber: isMultilaneEvent ? null : parseInt(laneNum),
              seedValue: athlete.seasonBest || athlete.seedValue || 0,
              assignmentOrder: assignments.length + 1
            });
          }
        });

        return {
          startListRoundID: heat.startListRoundID || null,
          roundType: activeRound,
          roundNumber: index + 1,
          scheduledStartTime: heat.scheduledStartTime || null,
          poolName: heat.poolName || null,
          assignments
        };
      })
      .filter(round => round.assignments.length > 0);

    const renumberedRounds = roundsArray.map((round, index) => ({
      ...round,
      roundNumber: index + 1
    }));

    const roundKey = activeRound.toLowerCase() + 's';
    
    return {
      eventTypeCode: parseInt(eventTypeCode),
      ageGroup,
      gender,
      [roundKey]: renumberedRounds
    };
  };

  // Save changes
  const handleSave = async () => {
    try {
      setSaving(true);
      const apiData = transformStateToApi();
      
      console.log('Saving data:', apiData);
      
      let response;
      switch (activeRound) {
        case 'Heat':
          response = await saveHeats(meetId, apiData);
          break;
        case 'Semi':
          response = await saveSemis(meetId, apiData);
          break;
        case 'Final':
          response = await saveFinals(meetId, apiData);
          break;
        default:
          throw new Error('Invalid round type');
      }

      const updatedRoundData = {
        heats: heats
      };
      
      setRoundsData(prev => ({
        ...prev,
        [activeRound]: updatedRoundData
      }));

      originalHeatsRef.current = JSON.stringify(heats);
      setHasUnsavedChanges(false);
      if (response.data) {
        toast.success('Start list updated successfully');
      }
    } catch (err) {
      toast.error('Error saving changes: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscardChanges = () => {
    if (originalHeatsRef.current) {
      setHeats(JSON.parse(originalHeatsRef.current));
      setHasUnsavedChanges(false);
    }
  };

  // Update lanes when selectedLanes changes
  useEffect(() => {
    if (heats.length > 0 && selectedLanes.length > 0 && heatsInitialized.current) {
      setHeats(prevHeats => {
        return prevHeats.map(heat => {
          const newLanes = {};
          selectedLanes.forEach(lane => {
            newLanes[lane] = heat.lanes[lane] || null;
          });
          return { ...heat, lanes: newLanes };
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanes]);

  // Toggle lane selection
  // const toggleLane = (lane) => {
  //   const isDeselecting = selectedLanes.includes(lane);
    
  //   setSelectedLanes((prev) => {
  //     const newSelectedLanes = isDeselecting
  //       ? prev.filter((l) => l !== lane)
  //       : [...prev, lane].sort((a, b) => a - b);
      
  //     const numSelectedLanes = newSelectedLanes.length;
  //     if (numSelectedLanes > 0) {
  //       const newMinAthletes = Math.max(2, Math.min(minAthletes, numSelectedLanes));
  //       setMinAthletes(newMinAthletes);
        
  //       if (maxAthletes > numSelectedLanes) {
  //         setMaxAthletes(numSelectedLanes);
  //       }
        
  //       if (newMinAthletes > maxAthletes) {
  //         setMaxAthletes(newMinAthletes);
  //       }
  //     } else {
  //       setMinAthletes(2);
  //       setMaxAthletes(8);
  //     }
      
  //     return newSelectedLanes;
  //   });

  //   if (isDeselecting) {
  //     const athletesToReturn = [];
  //     heats.forEach(heat => {
  //       if (heat.lanes[lane]) {
  //         athletesToReturn.push(heat.lanes[lane]);
  //       }
  //     });
      
  //     if (athletesToReturn.length > 0) {
  //       setAvailableAthletes(prev => [...prev, ...athletesToReturn]);
  //     }
  //   }
  // };

  // UPDATED: Handle lane click (assign/move athletes) - remove max check for group mode
  const handleLaneClick = (heatIndex, lane) => {
    const targetAthlete = heats[heatIndex].lanes[lane];

    if (targetAthlete && !selectedAthlete && !selectedLaneAthlete) {
      setSelectedLaneAthlete({ heatIndex, lane, athlete: targetAthlete });
      return;
    }

    if (selectedAthlete && !targetAthlete) {
      // Only check max for multilane events
      if (isMultilaneEvent) {
        const currentAthletesInHeat = Object.values(heats[heatIndex].lanes).filter(a => a !== null).length;
        if (currentAthletesInHeat >= maxAthletes) {
          return;
        }
      }
      
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[lane] = selectedAthlete;
      setHeats(newHeats);
      setAvailableAthletes((prev) =>
        prev.filter((a) => a.athleteID !== selectedAthlete.athleteID)
      );
      setSelectedAthlete(null);
      return;
    }

    if (selectedAthlete && targetAthlete) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[lane] = selectedAthlete;
      setHeats(newHeats);
      setAvailableAthletes((prev) =>
        prev.filter((a) => a.athleteID !== selectedAthlete.athleteID).concat(targetAthlete)
      );
      setSelectedAthlete(null);
      return;
    }

    if (selectedLaneAthlete && !targetAthlete && selectedLaneAthlete.heatIndex === heatIndex) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[selectedLaneAthlete.lane] = null;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    if (selectedLaneAthlete && targetAthlete && selectedLaneAthlete.heatIndex === heatIndex) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[selectedLaneAthlete.lane] = targetAthlete;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    if (selectedLaneAthlete && !targetAthlete && selectedLaneAthlete.heatIndex !== heatIndex) {
      const newHeats = [...heats];
      newHeats[selectedLaneAthlete.heatIndex].lanes[selectedLaneAthlete.lane] = null;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    if (selectedLaneAthlete && targetAthlete && selectedLaneAthlete.heatIndex !== heatIndex) {
      const newHeats = [...heats];
      newHeats[selectedLaneAthlete.heatIndex].lanes[selectedLaneAthlete.lane] = targetAthlete;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    if (selectedLaneAthlete && selectedLaneAthlete.heatIndex === heatIndex && selectedLaneAthlete.lane === lane) {
      setSelectedLaneAthlete(null);
      return;
    }
  };

  // Remove athlete from lane
  const removeAthlete = (heatIndex, lane) => {
    const athlete = heats[heatIndex].lanes[lane];
    if (!athlete) return;

    const newHeats = [...heats];
    newHeats[heatIndex].lanes[lane] = null;
    setHeats(newHeats);
    setAvailableAthletes((prev) => [...prev, athlete]);
    setSelectedLaneAthlete(null);
  };

  // Add new heat
  const addHeat = () => {
    if (selectedLanes.length === 0 && isMultilaneEvent) return;
    
    const positions = isMultilaneEvent 
      ? selectedLanes 
      : Array.from({ length: 30 }, (_, i) => i + 1);
    
    const newHeat = {
      id: heats.length + 1,
      roundType: activeRound,
      roundNumber: heats.length + 1,
      lanes: Object.fromEntries(positions.map((l) => [l, null])),
    };
    setHeats([...heats, newHeat]);
  };

  // Remove heat
  const removeHeat = (heatIndex) => {
    if (heatIndex === 0) return;

    const heat = heats[heatIndex];
    const athletesToReturn = Object.values(heat.lanes).filter((a) => a !== null);

    setHeats((prev) => prev.filter((_, i) => i !== heatIndex));
    setAvailableAthletes((prev) => [...prev, ...athletesToReturn]);
    setSelectedLaneAthlete(null);
  };

  // Clear heat
  const clearHeat = (heatIndex) => {
    const heat = heats[heatIndex];
    const athletesToReturn = Object.values(heat.lanes).filter((a) => a !== null);

    const positions = isMultilaneEvent 
      ? selectedLanes 
      : Array.from({ length: 30 }, (_, i) => i + 1);

    const newHeats = [...heats];
    newHeats[heatIndex].lanes = Object.fromEntries(positions.map((l) => [l, null]));
    setHeats(newHeats);
    setAvailableAthletes((prev) => [...prev, ...athletesToReturn]);
  };

  // Clear all heats
  const clearAllHeats = () => {
    const allAssignedAthletes = [];
    heats.forEach(heat => {
      Object.values(heat.lanes).forEach(athlete => {
        if (athlete) allAssignedAthletes.push(athlete);
      });
    });

    const positions = isMultilaneEvent 
      ? selectedLanes 
      : Array.from({ length: 30 }, (_, i) => i + 1);

    const clearedHeats = heats.map(heat => ({
      ...heat,
      lanes: Object.fromEntries(positions.map((l) => [l, null]))
    }));

    setHeats(clearedHeats);
    setAvailableAthletes(allAthletes);
  };

  const assignedCount = heats.reduce((total, heat) => {
    return total + Object.values(heat.lanes).filter((a) => a !== null).length;
  }, 0);

  const handleBack = () => {
    navigate(routes.admin.meetStartLists.url(clubId, meetId));
  };

  return (
    <AdminTablePageLayout loading={loading}>
      <PageHeader
        title={eventDetails ? `${eventDetails.eventTypeName} ${eventDetails.groupName || formatGroupRange(groupingBasis, eventDetails.ageGroup)} ${eventDetails.gender} - Start List` : 'Loading...'}
        subTitle={eventDetails ? `${allAthletes.length} eligible athletes` : ''}
      >
        <button onClick={handleBack} className={styles.backLink}>
          Back to Event List
        </button>
      </PageHeader>

      <div className={styles.editorCard}>
        <div className={styles.tabsSection}>
          <div className={styles.roundTabs}>
            {ROUND_TYPES.map((round) => (
              <button
                key={round}
                onClick={() => handleRoundClick(round)}
                className={`${styles.tabButton} ${activeRound === round ? styles.tabButtonActive : ''}`}
              >
                {round}
                {roundsData[round]?.heats?.length > 0 && (
                  <span style={{ marginLeft: '4px' }}>
                    ({roundsData[round].heats.length})
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className={styles.actionButtons}>
            <CustomNewButton onClick={addHeat} variant="primary2" small>
              <Plus size={16} />
              Add {isMultilaneEvent ? 'Heat' : 'Group'}
            </CustomNewButton>
            <CustomNewButton
              onClick={clearAllHeats}
              variant=""
              small
              customStyle={{ 
                backgroundColor: 'white',
                color: '#dc2626',
                border: '1px solid #fca5a5'
              }}
            >
              Clear All
            </CustomNewButton>
          </div>
        </div>

        <div className={styles.separator}></div>

        <div className={styles.mainEditor}>
          <AthletesPanel
            availableAthletes={availableAthletes}
            selectedAthlete={selectedAthlete}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onAthleteSelect={(athlete) => {
              setSelectedAthlete(athlete);
              setSelectedLaneAthlete(null);
            }}
          />

          <HeatsSection
            heats={heats}
            selectedLanes={selectedLanes}
            selectedLaneAthlete={selectedLaneAthlete}
            onLaneClick={handleLaneClick}
            onRemoveAthlete={removeAthlete}
            onClearHeat={clearHeat}
            onRemoveHeat={removeHeat}
            isMultilaneEvent={isMultilaneEvent}
          />
        </div>

        <div className={styles.separator}></div>

        <div className={styles.statusBar}>
          <div className={styles.statusText}>
            Total: {allAthletes.length} athletes | Assigned: {assignedCount} | Unassigned: {availableAthletes.length}
            {hasUnsavedChanges && <span style={{ color: '#f59e0b', marginLeft: '12px', fontWeight: 600 }}>• Unsaved changes</span>}
          </div>
          <div className={styles.statusActions}>
            <CustomNewButton 
              variant="outline" 
              small
              onClick={handleDiscardChanges}
              disabled={!hasUnsavedChanges}
            >
              Discard Changes
            </CustomNewButton>
            <CustomNewButton 
              variant="success" 
              small
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </CustomNewButton>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>Unsaved Changes</h2>
            <p className={styles.modalDescription}>
              You have unsaved changes to your {activeRound} assignments. What would you like to do?
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalButton}
                onClick={() => {
                  setShowUnsavedModal(false);
                  setPendingRound(null);
                }}
              >
                Cancel
              </button>
              <button
                className={styles.modalButton}
                onClick={handleDiscardAndSwitch}
              >
                Discard Changes
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                onClick={handleSaveAndSwitch}
              >
                Save & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      <AutoGenerateModal
        isOpen={showAutoGenerate}
        onClose={() => setShowAutoGenerate(false)}
        onGenerate={() => {
          console.log('Auto generate heats');
        }}
      />
    </AdminTablePageLayout>
  );
};

export default StartListEditor;